import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { useRelayEnvironment } from 'react-relay';

import FlashesStore from 'src/stores/FlashesStore';
import logger from 'src/util/logger';

import { getPortfolioDataInBatches } from '../helpers/dataBatching';
import {
  addSegmentToCache,
  buildCacheAggregationKey,
  clearCache,
  computeCacheMetadata,
  getPortfolioDataFromCache,
  lookupCache,
} from '../helpers/dataCaching';
import { PortfolioShowLoadingActions } from '../reducer/constants';

/**
 * Custom hook that encapsulates all portfolio data fetching.
 * Two-effect pattern:
 *   Initial-load effect (portfolioId) — full load: meters, rules, energy, trade summaries.
 *   Date-range effect (initialLoadComplete + dateSpan) — date-range change: checks the
 *     read-through cache in dataCaching.js. On cache miss, runs the full 5-stage
 *     pipeline (including meters) via getPortfolioDataInBatches.
 * @param {object} params
 * @param {string} params.portfolioId - Portfolio UUID
 * @param {object} params.dateSpan - { start, finish } Luxon DateTimes
 * @param {string} params.timezone - Timezone string
 * @param {object} params.initialData - Static portfolio data (from Relay fragment)
 * @param {string} params.historianAggregation - Aggregation for energy data
 * @param {string} params.tradeAggregation - Aggregation for trade data
 * @param {Function} [params.stateDispatchFunc] - Reducer dispatch for loading state
 * @returns {object} { combinedData, cancelFunc }
 */
const usePortfolioData = ({
  portfolioId,
  dateSpan,
  timezone,
  initialData,
  historianAggregation,
  tradeAggregation,
  stateDispatchFunc,
}) => {
  const environment = useRelayEnvironment();

  const [combinedData, setCombinedData] = useState(null);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  // Synchronous mirror of initialLoadComplete so the initial-load effect can gate
  // the date-range effect within the same render pass (setState is async/batched).
  const initialLoadCompleteRef = useRef(false);

  const isFetchingData = useRef(false);
  const cancelTokenRef = useRef({ current: false });
  const cancelledDateSpanRef = useRef(null);
  const dateSpanRef = useRef(dateSpan);
  dateSpanRef.current = dateSpan;

  // Keep refs for values used inside the initial-load effect callbacks so they always
  // reflect the latest props. The initial-load effect only re-runs on portfolioId
  // change; these refs let the onDataReceived callback detect when
  // other params went stale while the request was in flight.
  const timezoneRef = useRef(timezone);
  timezoneRef.current = timezone;
  const historianAggregationRef = useRef(historianAggregation);
  historianAggregationRef.current = historianAggregation;
  const tradeAggregationRef = useRef(tradeAggregation);
  tradeAggregationRef.current = tradeAggregation;

  // Tracks the dateSpan that the initial-load effect loaded, so the date-range effect
  // can skip the duplicate fetch when initialLoadComplete first becomes true.
  const initialDateSpanRef = useRef(null);

  // Clear cache on unmount
  useEffect(() => () => {
    clearCache();
  }, []);

  // ── Initial-load effect: Full initial load ────────────────────
  useEffect(() => {
    if (!portfolioId) {
      logger.error('No portfolio ID found for portfolio data fetching');
      return undefined;
    }

    logger.log('Effect 1: full initial load for portfolio:', portfolioId);

    // Reset for the new portfolio so the date-range effect does not run in parallel
    // with the initial-load effect when portfolioId changes without unmounting.
    // The ref is cleared synchronously so the date-range effect sees it in the same
    // render pass; the state update triggers a re-render for the dep array.
    initialLoadCompleteRef.current = false;
    setInitialLoadComplete(false);
    setCombinedData(null);
    initialDateSpanRef.current = null;

    if (stateDispatchFunc) {
      stateDispatchFunc({ type: PortfolioShowLoadingActions.START_REQUEST });
    }

    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    getPortfolioDataInBatches({
      portfolioId,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      dispatchFunc: stateDispatchFunc,
      onDataReceived: (mergedData) => {
        logger.log('Received merged portfolio data');
        initialDateSpanRef.current = dateSpan;
        // Seed the cache so the date-range effect can serve subsets from it
        lookupCache(portfolioId);
        const cacheKey = buildCacheAggregationKey(
          historianAggregation,
          tradeAggregation,
          timezone,
        );
        addSegmentToCache(dateSpan, mergedData, cacheKey);

        // If the user changed dateSpan, timezone, or aggregation while the
        // request was in flight, the fetched data is for stale params.
        // Cache it (it's still valid for those params) but skip the state
        // commit — the date-range effect will handle the current params once
        // initialLoadComplete is set.
        const startChanged = dateSpanRef.current?.start?.toMillis()
          !== dateSpan?.start?.toMillis();
        const finishChanged = dateSpanRef.current?.finish?.toMillis()
          !== dateSpan?.finish?.toMillis();
        const paramsStale = startChanged || finishChanged
          || timezoneRef.current !== timezone
          || historianAggregationRef.current !== historianAggregation
          || tradeAggregationRef.current !== tradeAggregation;

        if (!paramsStale) {
          setCombinedData(mergedData);
        } else {
          logger.log('Skipping state commit — params changed during initial load');
        }
        initialLoadCompleteRef.current = true;
        setInitialLoadComplete(true);
      },
      onError: (fetchError, message) => {
        logger.error('Error fetching portfolio data:', fetchError);
        FlashesStore.flash(
          FlashesStore.ERROR,
          message || 'Error fetching portfolio data',
        );
      },
    });

    return () => {
      cancelToken.current = true;
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PortfolioShowLoadingActions.CANCEL_REQUEST });
      }
    };
  }, [portfolioId, stateDispatchFunc]);

  // ── Date-range effect: Date-range change (time-series only) ───
  useEffect(() => {
    if (!portfolioId || !dateSpan) return undefined;
    // Use the ref (not the state) so this check works even in the same
    // render pass where the initial-load effect reset it for a new portfolioId.
    if (!initialLoadCompleteRef.current) return undefined;

    // Skip when the date-range effect fires for the first time after the
    // initial-load effect completes — it already fetched this dateSpan.
    const initialSpan = initialDateSpanRef.current;
    if (initialSpan
      && dateSpan?.start?.toMillis() === initialSpan.start?.toMillis()
      && dateSpan?.finish?.toMillis() === initialSpan.finish?.toMillis()) {
      logger.log('Skipping fetch - dateSpan already loaded by initial fetch');
      initialDateSpanRef.current = null;
      return undefined;
    }
    initialDateSpanRef.current = null;

    // Skip when triggered by cancelFunc rather than a genuine dateSpan change
    const cancelledDateSpan = cancelledDateSpanRef.current;
    cancelledDateSpanRef.current = null;

    if (cancelledDateSpan
      && dateSpan?.start?.toMillis() === cancelledDateSpan.start?.toMillis()
      && dateSpan?.finish?.toMillis() === cancelledDateSpan.finish?.toMillis()) {
      logger.log('Skipping fetch - datespan unchanged since cancellation');
      return undefined;
    }

    logger.log('Effect 2: date-range change', dateSpan?.start?.toISO(), '→', dateSpan?.finish?.toISO());

    const cancelToken = { current: false };
    cancelTokenRef.current = cancelToken;

    getPortfolioDataFromCache({
      portfolioId,
      dateSpan,
      timezone,
      environment,
      historianAggregation,
      tradeAggregation,
      initialData,
      isFetchingRef: isFetchingData,
      cancelledRef: cancelToken,
      dispatchFunc: stateDispatchFunc,
      onBeforeFetchFunc: () => {
        // Cache miss — dispatch START_REQUEST before the full pipeline
        // begins. SET_PORTFOLIO_LOADED is dispatched by
        // getPortfolioDataInBatches itself.
        if (stateDispatchFunc) {
          stateDispatchFunc({ type: PortfolioShowLoadingActions.START_REQUEST });
        }
      },
      onDataReceivedFunc: (mergedData, fromCache) => {
        // Guard: if params changed while a cache-miss fetch was in flight,
        // the data is for a stale range. The cleanup already cancelled the
        // token, but there is a small window between the last token check
        // and this callback. Discard the stale data — the new date-range effect
        // instance will handle the current params.
        const startStale = dateSpanRef.current?.start?.toMillis() !== dateSpan?.start?.toMillis();
        const finishStale = dateSpanRef.current?.finish?.toMillis()
          !== dateSpan?.finish?.toMillis();
        const dateRangeEffectParamsStale = startStale || finishStale
          || timezoneRef.current !== timezone
          || historianAggregationRef.current !== historianAggregation
          || tradeAggregationRef.current !== tradeAggregation;

        if (dateRangeEffectParamsStale) {
          logger.log('Skipping state commit — date-range effect params changed during fetch');
          return;
        }

        if (fromCache && stateDispatchFunc) {
          // Cache hit — replay the full loading sequence with real metadata
          // so the overlay shows meter counts, rule counts, etc.
          stateDispatchFunc({ type: PortfolioShowLoadingActions.START_REQUEST });
          stateDispatchFunc({ type: PortfolioShowLoadingActions.SET_PORTFOLIO_LOADED });

          const { metersCount, rulesCount } = computeCacheMetadata(mergedData);
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.SET_METERS_LOADED,
            metersCount,
          });
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.SET_RULES_LOADED,
            rulesCount,
          });
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.SET_CONSUMED_DATA_LOADED,
          });
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.SET_GENERATED_DATA_LOADED,
          });
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.SET_TRADE_SUMMARY_PROGRESS,
            batchesLoaded: 1,
            batchesTotal: 1,
          });
          stateDispatchFunc({
            type: PortfolioShowLoadingActions.MARK_FULLY_LOADED,
            metersCount,
            rulesCount,
            batchesLoaded: 1,
            batchesTotal: 1,
          });
        }
        setCombinedData(mergedData);
      },
      onErrorFunc: (fetchError, message) => {
        logger.error('Error fetching portfolio time-series data:', fetchError);
        FlashesStore.flash(
          FlashesStore.ERROR,
          message || 'Error fetching portfolio data',
        );
      },
    });

    return () => {
      cancelToken.current = true;
      if (stateDispatchFunc) {
        stateDispatchFunc({ type: PortfolioShowLoadingActions.CANCEL_REQUEST });
      }
    };
  }, [
    initialLoadComplete,
    portfolioId,
    // Luxon DateTime objects are compared by reference (Object.is) in
    // React dep arrays. Since PortfolioShow rebuilds the dateSpan object
    // on each render, the deps appear "changed" even when the date is
    // the same, causing duplicate fetches. Using toMillis() produces a
    // primitive number so the effect only re-runs when the date changes.
    dateSpan?.start?.toMillis(),
    dateSpan?.finish?.toMillis(),
    timezone,
    environment,
    historianAggregation,
    tradeAggregation,
    stateDispatchFunc,
  ]);

  const cancelFunc = useCallback(() => {
    cancelTokenRef.current.current = true;
    isFetchingData.current = false;
    cancelledDateSpanRef.current = {
      start: dateSpanRef.current?.start,
      finish: dateSpanRef.current?.finish,
    };
    initialLoadCompleteRef.current = true;
    setInitialLoadComplete(true);
    logger.log('Portfolio data fetch cancelled by user');
  }, []);

  return {
    combinedData,
    cancelFunc,
  };
};

export default usePortfolioData;
